import Swiper, {Autoplay, Navigation} from 'swiper';
import {swiperObserver} from '../../utils/swiper.utils.js';

const initializedWrappers = new WeakSet();
const equalizersRegistry = new WeakMap();
const rowTopTolerance = 4;
const equalizedSelectors = [
    '.audience-card__label',
    '.audience-card__title',
];

const getAudienceSlides = (swiperEl) => {
    return Array.from(
        swiperEl.querySelectorAll('.swiper-wrapper > .swiper-slide')
    );
};

const getAudienceSlideRows = (swiperEl) => {
    const slides = getAudienceSlides(swiperEl);
    const swiper = swiperEl.swiper;

    if (!slides.length) {
        return [];
    }

    if (swiper && !swiper.destroyed) {
        return [slides];
    }

    const rows = [];

    slides.forEach((slide) => {
        const top = slide.getBoundingClientRect().top;
        const existingRow = rows.find((row) => {
            return Math.abs(row.top - top) <= rowTopTolerance;
        });

        if (existingRow) {
            existingRow.slides.push(slide);
            existingRow.top = Math.min(existingRow.top, top);
            return;
        }

        rows.push({
            top,
            slides: [slide],
        });
    });

    return rows
        .sort((a, b) => a.top - b.top)
        .map((row) => row.slides);
};

const getAudienceEqualizerGroups = (swiperEl) => {
    const rows = getAudienceSlideRows(swiperEl);
    const groups = [];

    rows.forEach((row) => {
        equalizedSelectors.forEach((selector) => {
            const elements = row
                .map((slide) => slide.querySelector(selector))
                .filter(Boolean);

            if (elements.length) {
                groups.push(elements);
            }
        });
    });

    return groups;
};

const setupAudienceEqualizer = (swiperEl) => {
    const equalizer = equalizersRegistry.get(swiperEl);

    if (equalizer) {
        equalizer.refresh();
        return;
    }

    if (typeof window.lineTitlesFunc !== 'function') {
        return;
    }

    equalizersRegistry.set(
        swiperEl,
        window.lineTitlesFunc([], {
            getGroups: () => getAudienceEqualizerGroups(swiperEl),
        })
    );
};

const initAudienceSwiper = (scope = document) => {
    const audienceSwiperWr = scope.querySelectorAll('.audience-swiper-wr');

    audienceSwiperWr.forEach((el) => {
        if (initializedWrappers.has(el)) {
            const swiperEl = el.querySelector('.swiper');

            if (swiperEl) {
                setupAudienceEqualizer(swiperEl);
            }

            return;
        }

        const swiperEl = el.querySelector('.swiper');

        if (!swiperEl) {
            return;
        }

        const nextEl = el.querySelector('.swiper-button-next');
        const prevEl = el.querySelector('.swiper-button-prev');
        const slidesCount = getAudienceSlides(swiperEl).length;

        if (!slidesCount) {
            return;
        }

        initializedWrappers.add(el);

        if (slidesCount > 3) {
            if (swiperEl.swiper && !swiperEl.swiper.destroyed) {
                swiperEl.swiper.update();
            } else {
                const swiper = new Swiper(swiperEl, {
                    modules: [Autoplay, Navigation],
                    observer: true,
                    observeParents: true,
                    spaceBetween: 19,
                    slidesPerView: 1,
                    threshold: 10,
                    navigation: nextEl && prevEl ? {
                        nextEl: nextEl,
                        prevEl: prevEl,
                    } : undefined,
                    breakpoints: {
                        552: {
                            spaceBetween: 20,
                            slidesPerView: 2,
                        },
                        740: {
                            spaceBetween: 20,
                            slidesPerView: 3,
                        },
                        1260: {
                            spaceBetween: 45,
                            slidesPerView: 3,
                        },
                    },
                    autoplay: {
                        delay: 4000,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: window.innerWidth >= 1033,
                    }
                });

                swiperObserver(swiper);
            }
        } else {
            swiperEl.classList.add('no-swiper-type');
        }

        setupAudienceEqualizer(swiperEl);
    });
};

initAudienceSwiper();
window.initAudienceSwiper = initAudienceSwiper;
